/**
 * The Cloud board's visual grammar, in one place so the tiles, the workload
 * table, and the subscription grid can never disagree with each other.
 *
 * The backend already normalises ARM's many status strings into a small `state`
 * union (`nova.cloud.service.normalise_state`); this module only maps that
 * state onto design-system tones and formats the numbers Azure returns.
 */

import type { CloudWorkload } from "@/lib/api";
import {
  formatCount as formatCountShared,
  formatCurrency,
  formatPercent as formatPercentShared,
  SEVERITY_ORDER,
  SEVERITY_STATUS,
  type StatusTone,
} from "@/lib/charts/config";

/** Normalised workload state, as the backend defines it. */
export type WorkloadState = CloudWorkload["state"];

/** Tone shared by `Badge`, `Statistic`, `Card`, and `MiniSparkline`. */
export type { StatusTone } from "@/lib/charts/config";

/** Finance-palette tone used by `StatisticBar` and `StatusStrip`. */
export type { StripTone } from "@/lib/charts/config";

export const STATE_LABEL: Record<WorkloadState, string> = {
  running: "Running",
  stopped: "Stopped",
  provisioning: "Deploying",
  failed: "Failed",
  unknown: "Unknown",
};

/**
 * A stopped resource is `warning`, not `danger`: plenty of the estate's stopped
 * App Services are dev functions somebody switched off on purpose. It is real
 * signal worth surfacing, but it is not an outage — `failed` is.
 */
export const STATE_TONE: Record<WorkloadState, StatusTone> = {
  running: "success",
  stopped: "warning",
  provisioning: "neutral",
  failed: "danger",
  unknown: "neutral",
};

/** Segment colours for the composition bar — DS tokens, never raw hex. */
export const STATE_COLOR: Record<WorkloadState, string> = {
  running: "var(--norican-success)",
  stopped: "var(--norican-warning)",
  provisioning: "var(--norican-info)",
  failed: "var(--norican-danger)",
  unknown: "var(--norican-bg-muted)",
};

/** Order used wherever states are listed, problems first. */
export const STATE_ORDER: readonly WorkloadState[] = [
  "failed",
  "stopped",
  "provisioning",
  "unknown",
  "running",
];

/** Azure Resource Health's own vocabulary, mapped onto DS tones. */
export const AVAILABILITY_TONE: Record<string, StatusTone> = {
  Available: "success",
  Degraded: "warning",
  Unavailable: "danger",
  Unknown: "neutral",
};

/** Advisor impact, mapped onto DS tones. */
export const IMPACT_TONE: Record<string, StatusTone> = {
  High: "danger",
  Medium: "warning",
  Low: "neutral",
};

/** Service Health event types, mapped onto DS tones. */
export const EVENT_TONE: Record<string, StatusTone> = {
  ServiceIssue: "danger",
  PlannedMaintenance: "neutral",
  HealthAdvisory: "warning",
  SecurityAdvisory: "danger",
};

/** Human labels for Azure's event-type enum. */
export const EVENT_LABEL: Record<string, string> = {
  ServiceIssue: "Service issue",
  PlannedMaintenance: "Planned maintenance",
  HealthAdvisory: "Health advisory",
  SecurityAdvisory: "Security advisory",
};

/**
 * Azure Monitor alert severities, mapped onto DS tones — derived from the
 * central severity status scale in `@/lib/charts/config` so badges, tiles and
 * charts can never disagree about what a Sev1 looks like.
 */
export const SEVERITY_TONE: Record<string, StatusTone> = Object.fromEntries(
  SEVERITY_ORDER.map((sev) => [sev, SEVERITY_STATUS[sev].tone]),
);

/** Human labels for Azure Monitor's severity enum. */
export const SEVERITY_LABEL: Record<string, string> = Object.fromEntries(
  SEVERITY_ORDER.map((sev) => [sev, SEVERITY_STATUS[sev].label]),
);

/**
 * Change types from the resource change feed, mapped onto DS tones. A delete
 * is the change most worth a second look; creates and updates are routine.
 */
export const CHANGE_TONE: Record<string, StatusTone> = {
  Create: "success",
  Update: "neutral",
  Delete: "danger",
};

/** Sort comparator putting the workloads that need attention at the top. */
export function bySeverity(a: CloudWorkload, b: CloudWorkload): number {
  const rank = STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);
  return rank !== 0 ? rank : a.name.localeCompare(b.name);
}

/** Thousands-separated integer, or an em dash when there is nothing to show. */
export function formatCount(value: number | null | undefined): string {
  return formatCountShared(value);
}

/**
 * A money amount in its currency, or an em dash when there is nothing to show.
 *
 * Cost Management reports whole subscriptions, so cents are noise — round to
 * whole units. Falls back to a plain number when the currency is unknown or
 * mixed across subscriptions (the backend then sends `currency: null`).
 */
export function formatMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
): string {
  return formatCurrency(value, currency);
}

/** A 0–100 value as a percentage with one decimal, or an em dash. */
export function formatPercent(value: number | null | undefined): string {
  return formatPercentShared(value);
}

/** The bare resource name from a full ARM resource ID. */
export function resourceNameFromId(resourceId: string | null | undefined): string {
  if (!resourceId) {
    return "—";
  }
  const segments = resourceId.split("/").filter(Boolean);
  return segments[segments.length - 1] ?? resourceId;
}

/** Compact `day` (an ISO date) as e.g. `04 Aug`, or an em dash. */
export function formatDay(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return parsed.toLocaleDateString([], { day: "2-digit", month: "short" });
}

/**
 * Render an Azure Monitor value in the unit a human reads it in.
 *
 * Azure reports CPU for Container Apps in nanocores and memory in bytes, both
 * of which are unreadable raw — 2,374,577 nanocores is 2.4 millicores.
 */
export function formatMetric(metricName: string, value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }
  if (metricName === "UsageNanoCores") {
    const millicores = value / 1_000_000;
    return millicores >= 1000
      ? `${(millicores / 1000).toFixed(2)} cores`
      : `${millicores.toFixed(1)} mcores`;
  }
  if (metricName === "WorkingSetBytes") {
    return formatBytes(value);
  }
  if (metricName.endsWith("_percent")) {
    return `${value.toFixed(1)}%`;
  }
  if (metricName === "HttpResponseTime") {
    return `${(value * 1000).toFixed(0)} ms`;
  }
  return value >= 100 ? Math.round(value).toLocaleString() : value.toFixed(1);
}

/** Bytes in the largest unit that keeps the number small. */
export function formatBytes(bytes: number): string {
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** Compact absolute timestamp — the board states when, never "recently". */
export function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return parsed.toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Strip the `microsoft.` prefix so a table cell reads as `web/sites`. */
export function shortType(resourceType: string): string {
  return resourceType.replace(/^microsoft\./, "");
}

/**
 * A signed change, or an em dash when the movement is unknown.
 *
 * The sign carries the meaning: `+12` and `12` read the same at a glance, and
 * an estate that lost twelve resources is a different story from one that
 * gained them. An unknown delta (no earlier day recorded yet) is an em dash,
 * never a zero.
 */
export function formatDelta(value: number | null | undefined): string {
  if (value == null) {
    return "—";
  }
  return value > 0 ? `+${value.toLocaleString()}` : value.toLocaleString();
}
